import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Mail, Phone, Heart, ChevronDown, ChevronUp } from 'lucide-react';

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('booked');
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  const statusColors = {
    booked: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Upcoming' },
    completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (id, status) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/bookings/${id}`, { serviceStatus: status });
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking:', error);
    }
  };

  const BookingCard = ({ booking }) => {
    const isExpanded = expandedBooking === booking._id;
    const statusStyle = statusColors[booking.serviceStatus];
    
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-4">
          <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
            <div className="flex items-start space-x-4">
              <Calendar className="text-gray-400 hidden md:block" size={20} />
              <div className="flex-1">
                <h3 className="font-medium">{booking.name}</h3>
                <div className="mt-1">
                  <ul className="flex flex-wrap gap-2 text-sm font-medium text-gray-900">
                    {booking.subservices.map((subservice, index) => (
                      <li key={index} className="flex items-center">
                        {index > 0 && <span className="mr-2 text-gray-400">•</span>}
                        {subservice}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <p className="text-sm text-gray-500">
                    {new Date(booking.date).toLocaleDateString()} at {booking.time}
                  </p>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${statusStyle.bg} ${statusStyle.text} font-medium`}>
                    {statusStyle.label}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {booking.serviceStatus === ('booked') && (
                <button
                  onClick={() => updateBookingStatus(booking._id, 'completed')}
                  className="px-3 py-1 text-sm bg-black text-white rounded hover:bg-gray-800"
                >
                  Mark Complete
                </button>
              )}
              <button
                onClick={() => setExpandedBooking(isExpanded ? null : booking._id)}
                className="text-gray-400 hover:text-gray-600"
              >
                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
            </div>
          </div>
          
          {isExpanded && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-start space-x-2">
                  <Mail className="text-gray-400 mt-1" size={16} />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="text-sm break-all">{booking.email}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <Phone className="text-gray-400 mt-1" size={16} />
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="text-sm">{booking.phone}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <Heart className="text-gray-400 mt-1" size={16} />
                  <div>
                    <p className="text-sm text-gray-600">Health Information</p>
                    <p className="text-sm">
                      Previous Therapy: {booking.previousMassageTherapy ? 'Yes' : 'No'}
                    </p>
                    <p className="text-sm">
                      On Medication: {booking.onMedication ? 'Yes' : 'No'}
                    </p>
                  </div>
                </div>
              </div>
              {booking.notes && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-2">Notes:</p>
                  <p className="text-sm text-gray-700">{booking.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const filteredBookings = bookings.filter(booking => booking.serviceStatus === filter);

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h2 className="text-2xl md:text-3xl font-bold">Massage Bookings</h2>
        <div className="flex gap-2 w-full md:w-auto">
          {['booked', 'completed'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`flex-1 md:flex-none px-3 py-1.5 text-sm rounded transition-colors ${
                filter === status
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {statusColors[status].label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="space-y-3">
          {filteredBookings.map(booking => (
            <BookingCard key={booking._id} booking={booking} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Bookings;